import React, {
  useCallback, useEffect, useState,
} from 'react'
import { useSelector } from 'react-redux'
import { AgentMessage, UserMessage } from '../OracleCommons/ChatMessage'
import { selectAuth } from '../../services/auth/selectors'
import {
  OracleThumbs,
  OracleThumbValue,
  useInitialScrollToBottom,
  useChatAutoScroll,
  useThumbsUpdate,
  OracleModerationMessage,
  ModerationError,
  OracleLoadingSpinner,
} from '../OracleCommons/index'
import { usePostHogEvent } from '../PostHog/index'
import { type GaiaDocument } from '../OracleCommons/gaiaTagParser'
import { getVideoPlayer } from '../OracleCommons/getVideoPlayer'
import { OracleGuideOnSideWelcome } from './OracleGuideOnSideWelcome'
import {
  GotsGetChatMessagesQuery,
  useGotsGetChatMessagesQuery,
} from './queries.generated'

/**
 * Props interface showing what data this component needs for chat display
 */
export interface OracleGuideOnSideContentProps {
  /** The chat session ID (if known). If present, will be used to fetch past messages. */
  chatSessionId?: string
  /** Current streaming text from AI */
  streamingText?: string
  /** Whether actively streaming */
  isStreaming?: boolean
  /** User's pending message being responded to */
  pendingMessage?: string
  /** Callback when scroll indicator visibility changes, includes scrollToBottom callback */
  onScrollIndicatorChange?: (visible: boolean, scrollToBottom: () => void) => void
  /** Moderation error from content policy violation */
  moderationError?: ModerationError | null
  /** Documents from current streaming response (for Gaia tag resolution) */
  streamingDocuments?: GaiaDocument[] | null
}

/**
 * Chat content display component
 *
 * Purpose: Display chat message history and streaming responses
 */
export const OracleGuideOnSideContent: React.FC<OracleGuideOnSideContentProps> = ({
  chatSessionId,
  pendingMessage,
  streamingText,
  isStreaming,
  onScrollIndicatorChange,
  moderationError,
  streamingDocuments,
}) => {
  const auth = useSelector(selectAuth)
  const trackMessageFeedback = usePostHogEvent('ai_message_feedback')
  const {
    data,
    // error,
    // loading,
    fetchMore,
  } = useGotsGetChatMessagesQuery({
    variables: {
      chatSessionId,
      // Load only most recent messages - this means we do not initially get the
      // entire history if it is a long conversation. Scroll up to fetch.
      // TODO(gabe): add 'scroll up to fetch more' functionality
      limit: 10,
      offset: 0,
      order: [['createdAt', 'DESC']], // Get newest messages first
    },
    context: {
      jwt: auth.get('jwt'),
      useMaster: true,
    },
    fetchPolicy: 'cache-and-network',
    skip: !chatSessionId, // Skip for new sessions without ID
  })

  const messages = makeMessagePairs(data?.oracleMessagePairList?.oracleMessagePairs)

  // Auto-scroll positioning during streaming
  const {
    spacerHeight, containerRef, pendingMessageRef, showStreamingUI, spacerRef,
  } = useChatAutoScroll({
    isStreaming,
    messagesLength: messages?.length ?? 0,
  })

  // Scroll to bottom on initial message load (from history only, not from streaming)
  useInitialScrollToBottom(containerRef, messages?.length ?? 0, chatSessionId, isStreaming)

  // Thumbs up/down state and mutation
  // Note: useThumbsUpdate handles rollback automatically on error
  const { updateThumb, getThumbValue } = useThumbsUpdate()

  // Scroll indicator state - tracks when content is scrolled above visible area
  const [showScrollIndicator, setShowScrollIndicator] = useState(false)

  // Scroll to bottom callback - used by scroll indicator
  const scrollToBottom = useCallback(() => {
    containerRef.current?.scrollTo({
      top: containerRef.current.scrollHeight,
      behavior: 'smooth',
    })
  }, [containerRef])

  // Update scroll indicator visibility based on content overflow
  const updateScrollIndicator = useCallback(() => {
    if (containerRef.current) {
      const container = containerRef.current
      const scrolledFromBottom = container.scrollHeight
        - container.scrollTop - container.clientHeight
      setShowScrollIndicator(scrolledFromBottom > 40)
    }
  }, [])

  // Fetch older messages when scrolling to top (pagination)
  const fetchOlderMessages = useCallback(async () => {
    if (!chatSessionId) {
      return
    }

    const currentMessages = data?.oracleMessagePairList?.oracleMessagePairs || []
    const limit = 10

    // Only fetch more if we have at least the limit number of messages
    if (currentMessages.length < limit) {
      return
    }

    const currentOffset = currentMessages.length

    // Store scroll position before fetching to prevent jump
    const container = containerRef.current
    const scrollTop = container?.scrollTop || 0
    const scrollHeight = container?.scrollHeight || 0

    try {
      await fetchMore({
        variables: {
          offset: currentOffset,
          limit,
          order: [['createdAt', 'DESC']], // Match initial query ordering
        },
        context: {
          jwt: auth.get('jwt'),
          useMaster: true,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev

          const newMessages = fetchMoreResult.oracleMessagePairList.oracleMessagePairs
          const existingMessages = prev.oracleMessagePairList.oracleMessagePairs

          // With DESC ordering, append older messages to existing messages
          const combinedMessages = [...existingMessages, ...newMessages]
          const uniqueMessages = combinedMessages.filter(
            (msg, index, arr) => arr.findIndex((m) => m.id === msg.id) === index,
          )

          return {
            ...prev,
            oracleMessagePairList: {
              ...prev.oracleMessagePairList,
              oracleMessagePairs: uniqueMessages,
            },
          }
        },
      })

      // Restore scroll position after new content loads to prevent jump
      requestAnimationFrame(() => {
        if (container) {
          const newScrollHeight = container.scrollHeight
          const heightDifference = newScrollHeight - scrollHeight
          container.scrollTop = scrollTop + heightDifference
        }
      })
    } catch {
      // Silently handle fetch errors - not critical if pagination fails
    }
  }, [
    fetchMore,
    auth,
    chatSessionId,
    data?.oracleMessagePairList?.oracleMessagePairs,
    containerRef,
  ])

  // Handle scroll to detect when user scrolls to top or away from bottom
  const handleScroll = useCallback(() => {
    if (containerRef.current) {
      const container = containerRef.current
      const currentScrollTop = container.scrollTop
      const isAtTop = currentScrollTop <= 10 // 10px threshold

      // Fetch more messages when scrolling to top
      if (isAtTop) {
        fetchOlderMessages()
      }

      updateScrollIndicator()
    }
  }, [fetchOlderMessages, updateScrollIndicator])

  // Attach scroll event listener
  useEffect(() => {
    const container = containerRef.current
    if (container) {
      container.addEventListener('scroll', handleScroll)
      return () => container.removeEventListener('scroll', handleScroll)
    }
    return undefined
  }, [handleScroll, containerRef])

  // Update scroll indicator when content changes (not just on scroll)
  useEffect(() => {
    const timer = setTimeout(updateScrollIndicator, 50)
    return () => clearTimeout(timer)
  }, [
    chatSessionId,
    streamingText,
    pendingMessage,
    isStreaming,
    data?.oracleMessagePairList?.oracleMessagePairs,
    moderationError,
    spacerHeight,
    updateScrollIndicator,
  ])

  // Notify parent of scroll indicator visibility changes
  useEffect(() => {
    onScrollIndicatorChange?.(showScrollIndicator, scrollToBottom)
  }, [showScrollIndicator, scrollToBottom, onScrollIndicatorChange])

  const onThumbsUp = (messagePairId: string, currentBackendValue: OracleThumbValue) => {
    // PostHog tracking
    trackMessageFeedback('Up')
    // GA4 tracking - handled by useThumbsUpdate hook
    updateThumb(messagePairId, 'up', currentBackendValue)
  }

  const onThumbsDown = (messagePairId: string, currentBackendValue: OracleThumbValue) => {
    // PostHog tracking
    trackMessageFeedback('Down')
    // GA4 tracking - handled by useThumbsUpdate hook
    updateThumb(messagePairId, 'down', currentBackendValue)
  }

  // Pause video when clicking links that open in a new tab (AI-492)
  const handleContentClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const anchor = (e.target as HTMLElement).closest('a[target="_blank"]')
    if (!anchor) return
    const player = getVideoPlayer()
    if (player && !player.paused?.()) {
      player.pause?.()
    }
  }, [])

  // Show welcome message when there's no chat history and not actively streaming
  const showWelcome = !chatSessionId && !isStreaming && !pendingMessage

  return (
    <div className="oracle-guide-on-side-layout__content-container" ref={containerRef} onClick={handleContentClick}>
      {/* Show welcome message for new sessions */}
      {showWelcome && (
        <OracleGuideOnSideWelcome className="oracle-guide-on-side-layout__welcome" />
      )}

      {/* Render finished messages */}
      {messages && messages.map((pair, idx) => {
        return <React.Fragment key={`msg-pair-${idx}`} >
          <UserMessage markdown={pair.userMessage} />
          <AgentMessage className="oracle-guide-on-side-layout__message-pad" markdown={pair.agentMessage} sameTabLinks={false} documents={pair.documents} />
          <OracleThumbs
            value={getThumbValue(pair.id, pair.thumbsValue as OracleThumbValue)}
            onThumbsUp={() => onThumbsUp(pair.id, pair.thumbsValue as OracleThumbValue)}
            onThumbsDown={() => onThumbsDown(pair.id, pair.thumbsValue as OracleThumbValue)}
            className="oracle-guide-on-side-layout__actions"
          />
        </React.Fragment>
      })}
      {showStreamingUI && pendingMessage && (
        <div ref={pendingMessageRef} className="oracle-guide-on-side-layout__pending-message">
          <UserMessage markdown={pendingMessage} />
          {streamingText
            ? <AgentMessage className="oracle-guide-on-side-layout__message-pad" markdown={streamingText} sameTabLinks={false} documents={streamingDocuments} />
            : <div className="oracle-gots-spinner"><OracleLoadingSpinner /></div>}
        </div>
      )}

      {/* Show moderation error with user message */}
      {moderationError && pendingMessage && !isStreaming && (
        <>
          <UserMessage markdown={pendingMessage} />
          <OracleModerationMessage
            severity={moderationError.config.severity}
            showCrisisResources={moderationError.config.showCrisisResources}
          />
        </>
      )}

      {/* Spacer to position user message at top during streaming */}
      {spacerHeight > 0 && (
        <div
          ref={spacerRef}
          className="oracle-guide-on-side-layout__streaming-spacer"
          style={{ height: `${spacerHeight}px`, flexShrink: 0 }}
        />
      )}
    </div>
  )
}

type GqlMessagePairs = GotsGetChatMessagesQuery['oracleMessagePairList']['oracleMessagePairs']

const makeMessagePairs = (gqlMessagePairs?: GqlMessagePairs) => {
  if (gqlMessagePairs == null) return []
  return gqlMessagePairs
    .map((gpair) => ({
      id: gpair.id,
      thumbsValue: gpair.thumbsValue,
      userMessage: gpair.userMessage.content,
      agentMessage: gpair.oracleMessage.content,
      documents: gpair.documents as GaiaDocument[],
    })).reverse()
}
